import type { CalEvent, UploadFile } from '../shared/types';
import { PASSWORD_RULES_TEXT, validatePassword } from '../shared/password';
import { api, errorMessage } from './api';
import { createCombobox, type ComboItem } from './combobox';
import { freqLabel, todayISO } from './dates';
import { computeDue, dueHint } from './due';
import { buildIcs, parseIcs, sha256Hex } from './ics';
import { loadGis, syncToGoogle } from './googleExport';
import { DEFAULT_SETTINGS, FONT_CHOICES, loadSettings, saveSettings } from './settings';
import { notifyChange, refreshData, state } from './state';
import { confirmDialog, field, focusFirst, h, openDialog, toast } from './ui';

async function saved(msg: string, dlg: { close(): void }): Promise<void> {
  await refreshData();
  notifyChange();
  dlg.close();
  toast(msg);
}

// --- Termin hinzufügen / bearbeiten (Feature 2 + 5) -----------------------------
export function openEventDialog(existing?: CalEvent): void {
  const dlg = openDialog(existing ? 'Termin ansehen / bearbeiten' : 'Termin hinzufügen');

  const items: ComboItem[] = computeDue(state.templates, state.events).map((d) => ({
    id: d.template.id,
    value: d.template.name,
    label: `${d.template.name} (${freqLabel(d.template.weeks)})`,
    hint: dueHint(d),
  }));
  const combo = createCombobox(items, { value: existing?.title ?? '', placeholder: 'Auswählen oder eintippen …' });
  const date = h('input', { type: 'date', required: true, value: existing?.date ?? todayISO() });
  const time = h('input', { type: 'time', value: existing?.time ?? '' });
  const desc = h('textarea', { rows: 4, value: existing?.description ?? '' });

  // „Erledigt von“: nur Benutzer, die sich anmelden können
  const userItems: ComboItem[] = [];
  const isIcs = existing?.source === 'ics'; // ICS-Termine können nicht erledigt werden
  const doneCombo = createCombobox(userItems, { value: existing?.doneBy ?? '', placeholder: 'Noch nicht erledigt' });
  doneCombo.input.id = 'ev-done';
  if (!isIcs) {
    api
    .users()
    .then((us) => us.forEach((u) => userItems.push({ id: u.name, value: u.name, label: u.name })))
    .catch((e: unknown) => toast(errorMessage(e), 'error'));
  }

  const save = async (): Promise<void> => {
    const title = combo.getText();
    if (!title) return toast('Bitte einen Namen für den Termin angeben.', 'error');
    if (!date.value) return toast('Bitte ein Datum auswählen.', 'error');
    const doneText = isIcs ? '' : doneCombo.getText();
    const doneUser = isIcs ? null : doneCombo.getSelected();
    if (doneText && !doneUser) return toast('„Erledigt von“: bitte einen der Benutzer auswählen oder das Feld leeren.', 'error');
    const payload = {
      title,
      doneBy: doneUser?.value,
      date: date.value,
      time: time.value || undefined,
      description: desc.value.trim() || undefined,
      templateId: combo.getSelected()?.id,
      // Individualtermin = nicht aus der Liste gewählt (gilt nicht für ICS-Termine)
      individual: isIcs ? undefined : combo.getSelected() === null,
    };
    try {
      if (existing) await api.updateEvent(existing.id, payload);
      else await api.createEvent(payload);
      await saved('Termin gespeichert', dlg);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  combo.input.id = 'ev-title';
  const comboField = h('div', { class: 'field' }, h('label', { for: 'ev-title' }, 'Termin'), combo.el);

  dlg.body.append(
    comboField,
    h('div', { class: 'row' }, field('Datum', date), field('Uhrzeit (optional)', time)),
    ...(isIcs ? [] : [h('div', { class: 'field' }, h('label', { for: 'ev-done' }, 'Erledigt von'), doneCombo.el)]),
    field('Details (optional)', desc),
    ...(existing?.source === 'ics' ? [h('p', { class: 'hint' }, 'Dieser Termin stammt aus einer ICS-Datei.')] : []),
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Abbrechen'),
      h('button', { type: 'button', class: 'btn btn-primary', onclick: () => void save() }, 'Speichern'),
    ),
  );
  focusFirst(dlg);
}

// --- Auswahltermine pflegen (Feature 6) -------------------------------------------
export function openTemplatesDialog(): void {
  const dlg = openDialog('Auswahltermine pflegen');
  const NEW = '__new__';
  const select = h('select');
  select.append(h('option', { value: NEW }, '＋ Neuen Auswahltermin anlegen'));
  const sorted = [...state.templates].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  for (const t of sorted) select.append(h('option', { value: t.id }, `${t.name} (${freqLabel(t.weeks)})`));

  const name = h('input', { type: 'text', maxlength: '100' });
  const weeks = h('input', { type: 'number', min: '1', max: '520', step: '1', value: '2' });
  const delBtn = h('button', { type: 'button', class: 'btn btn-danger' }, 'Löschen');

  const sync = (): void => {
    const t = state.templates.find((x) => x.id === select.value);
    name.value = t?.name ?? '';
    weeks.value = String(t?.weeks ?? 2);
    delBtn.disabled = !t;
  };
  select.addEventListener('change', sync);
  sync();

  const save = async (): Promise<void> => {
    const n = name.value.trim();
    const w = Number(weeks.value);
    if (!n) return toast('Bitte einen Namen eingeben.', 'error');
    if (!Number.isInteger(w) || w < 1) return toast('Die Frequenz muss eine ganze Zahl ab 1 sein.', 'error');
    try {
      if (select.value === NEW) await api.createTemplate({ name: n, weeks: w });
      else await api.updateTemplate(select.value, { name: n, weeks: w });
      await saved('Auswahltermin gespeichert', dlg);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  delBtn.addEventListener('click', async () => {
    const t = state.templates.find((x) => x.id === select.value);
    if (!t) return;
    if (!(await confirmDialog(`Auswahltermin „${t.name}“ wirklich löschen? Bereits eingetragene Termine bleiben erhalten.`))) return;
    try {
      await api.deleteTemplate(t.id);
      await saved('Auswahltermin gelöscht', dlg);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  });

  dlg.body.append(
    field('Auswahltermin', select),
    field('Name', name),
    field('Frequenz (in Wochen)', weeks),
    h(
      'div',
      { class: 'actions' },
      delBtn,
      h('span', { class: 'spacer' }),
      h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Abbrechen'),
      h('button', { type: 'button', class: 'btn btn-primary', onclick: () => void save() }, 'Speichern'),
    ),
  );
  focusFirst(dlg);
}

// --- ICS-Upload (Feature 4) ----------------------------------------------------------
interface Prepared extends UploadFile {
  ok: boolean;
}

export function openUploadDialog(): void {
  const dlg = openDialog('ICS-Dateien hochladen');
  let prepared: Prepared[] = [];

  const list = h('ul', { class: 'file-list' });
  const status = h('div', { class: 'status' });
  const uploadBtn = h('button', { type: 'button', class: 'btn btn-primary' }, 'Hochladen');
  uploadBtn.disabled = true;

  const pick = async (files: FileList | null): Promise<void> => {
    status.textContent = '';
    const icsFiles = Array.from(files ?? []).filter((f) => f.name.toLowerCase().endsWith('.ics'));
    prepared = [];
    list.replaceChildren();
    for (const f of icsFiles) {
      const text = await f.text();
      const events = parseIcs(text);
      const hash = await sha256Hex(`${f.name}\u0000${text}`);
      prepared.push({ hash, name: f.name, events, ok: events.length > 0 });
      list.append(
        h('li', { class: events.length ? '' : 'error' }, `${f.name} – ${events.length ? `${events.length} Termin(e)` : 'keine Termine gefunden'}`),
      );
    }
    if (!icsFiles.length) status.textContent = 'Keine .ics-Dateien ausgewählt.';
    uploadBtn.disabled = !prepared.some((p) => p.ok);
  };

  const filesInput = h('input', { type: 'file', accept: '.ics,text/calendar', multiple: true, hidden: true });
  const dirInput = h('input', { type: 'file', hidden: true });
  dirInput.setAttribute('webkitdirectory', '');
  filesInput.addEventListener('change', () => void pick(filesInput.files));
  dirInput.addEventListener('change', () => void pick(dirInput.files));

  uploadBtn.addEventListener('click', async () => {
    uploadBtn.disabled = true;
    status.textContent = 'Lade hoch …';
    try {
      const payload: UploadFile[] = prepared.filter((p) => p.ok).map((p) => ({ hash: p.hash, name: p.name, events: p.events }));
      const r = await api.upload(payload);
      await refreshData();
      notifyChange();
      status.textContent = `${r.addedFiles} Datei(en) mit ${r.addedEvents} Termin(en) hinzugefügt, ${r.skippedFiles} bereits vorhandene Datei(en) übersprungen.`;
      list.replaceChildren();
      prepared = [];
    } catch (e) {
      status.textContent = errorMessage(e);
      uploadBtn.disabled = false;
    }
  });

  dlg.body.append(
    h('p', { class: 'muted' }, 'Mehrere .ics-Dateien oder einen ganzen Ordner auswählen. Bereits hochgeladene Dateien werden erkannt und übersprungen.'),
    h(
      'div',
      { class: 'actions left' },
      h('button', { type: 'button', class: 'btn', onclick: () => filesInput.click() }, 'Dateien wählen …'),
      h('button', { type: 'button', class: 'btn', onclick: () => dirInput.click() }, 'Ordner wählen …'),
    ),
    filesInput,
    dirInput,
    list,
    status,
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Schließen'),
      uploadBtn,
    ),
  );
}

// --- Export (Feature 8) ---------------------------------------------------------------
export function openExportDialog(): void {
  const dlg = openDialog('Export nach Google Kalender');
  const status = h('div', { class: 'status' });
  const force = h('input', { type: 'checkbox', id: 'force-sync' });
  const syncBtn = h('button', { type: 'button', class: 'btn btn-primary' }, 'Mit Google Kalender abgleichen');
  syncBtn.disabled = true;
  let clientId = '';
  let calendarName = 'Franks Kalender';
  const intro = h('p', { class: 'muted' });
  const setIntro = (): void => {
    intro.textContent = `Alle Termine werden in einen eigenen Google-Kalender „${calendarName}“ übertragen. Jeder weitere Abgleich aktualisiert geänderte Termine und entfernt gelöschte.`;
  };
  setIntro();

  api
    .config()
    .then(async (c) => {
      clientId = c.googleClientId;
      calendarName = c.calendarName;
      setIntro();
      if (!clientId) {
        status.textContent = 'Google-Abgleich nicht eingerichtet: GOOGLE_CLIENT_ID fehlt (siehe README). Der Download als .ics-Datei funktioniert trotzdem.';
        return;
      }
      await loadGis();
      syncBtn.disabled = false;
    })
    .catch((e: unknown) => {
      status.textContent = errorMessage(e);
    });

  syncBtn.addEventListener('click', async () => {
    syncBtn.disabled = true;
    status.classList.remove('error');
    status.textContent = 'Warte auf Google-Anmeldung …';
    try {
      const r = await syncToGoogle(clientId, calendarName, state.events, force.checked, (m) => (status.textContent = m));
      status.textContent = `Fertig: ${r.synced} übertragen, ${r.unchanged} unverändert, ${r.deleted} gelöscht${r.failed ? `, ${r.failed} Fehler (siehe Konsole)` : ''}.`;
      if (r.failed) status.classList.add('error');
    } catch (e) {
      status.textContent = errorMessage(e);
      status.classList.add('error');
    } finally {
      syncBtn.disabled = false;
    }
  });

  const download = (): void => {
    const blob = new Blob([buildIcs(state.events)], { type: 'text/calendar;charset=utf-8' });
    const a = h('a', { href: URL.createObjectURL(blob), download: 'franks-kalender.ics' });
    document.body.append(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  dlg.body.append(
    intro,
    h('label', { class: 'check', for: 'force-sync' }, force, ' Alles neu übertragen (auch unveränderte Termine)'),
    h('div', { class: 'actions left' }, syncBtn, h('button', { type: 'button', class: 'btn', onclick: download }, '.ics-Datei herunterladen')),
    status,
    h('div', { class: 'actions' }, h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Schließen')),
  );
}

// --- Darstellung ------------------------------------------------------------------------
export function openSettingsDialog(): void {
  const dlg = openDialog('Darstellung');
  const s = loadSettings();

  const fontSelect = (current: string): HTMLSelectElement => {
    const sel = h('select');
    for (const f of FONT_CHOICES) sel.append(h('option', { value: f.value }, f.label));
    sel.value = current;
    if (sel.value !== current) sel.selectedIndex = 0;
    return sel;
  };
  const sizeInput = (v: number): HTMLInputElement => h('input', { type: 'number', min: '8', max: '32', step: '1', value: String(v) });

  const titleFont = fontSelect(s.titleFont);
  const titleSize = sizeInput(s.titleSize);
  const timeFont = fontSelect(s.timeFont);
  const timeSize = sizeInput(s.timeSize);

  const apply = (): void => {
    saveSettings({
      ...loadSettings(),
      titleFont: titleFont.value,
      titleSize: Number(titleSize.value) || DEFAULT_SETTINGS.titleSize,
      timeFont: timeFont.value,
      timeSize: Number(timeSize.value) || DEFAULT_SETTINGS.timeSize,
    });
    notifyChange();
    dlg.close();
  };

  dlg.body.append(
    h('h3', {}, 'Termin-Name'),
    h('div', { class: 'row' }, field('Schriftart', titleFont), field('Größe (px)', titleSize)),
    h('h3', {}, 'Uhrzeit'),
    h('div', { class: 'row' }, field('Schriftart', timeFont), field('Größe (px)', timeSize)),
    h(
      'div',
      { class: 'actions' },
      h(
        'button',
        {
          type: 'button',
          class: 'btn',
          onclick: () => {
            saveSettings({ ...DEFAULT_SETTINGS, range: loadSettings().range });
            notifyChange();
            dlg.close();
          },
        },
        'Zurücksetzen',
      ),
      h('span', { class: 'spacer' }),
      h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Abbrechen'),
      h('button', { type: 'button', class: 'btn btn-primary', onclick: apply }, 'Übernehmen'),
    ),
  );
}

// --- Passwort ändern ------------------------------------------------------------------
export function openPasswordDialog(): void {
  const dlg = openDialog('Passwort ändern');
  const pw = h('input', { type: 'password', autocomplete: 'new-password' });
  const pw2 = h('input', { type: 'password', autocomplete: 'new-password' });
  const msg = h('div', { class: 'error' });

  const save = async (): Promise<void> => {
    msg.textContent = '';
    const err = validatePassword(pw.value);
    if (err) {
      msg.textContent = err;
      return;
    }
    if (pw.value !== pw2.value) {
      msg.textContent = 'Die Passwörter stimmen nicht überein.';
      return;
    }
    try {
      await api.changePassword(pw.value);
      dlg.close();
      toast('Passwort geändert');
    } catch (e) {
      msg.textContent = errorMessage(e);
    }
  };

  dlg.body.append(
    field('Neues Passwort', pw),
    field('Wiederholen', pw2),
    h('p', { class: 'hint' }, PASSWORD_RULES_TEXT),
    msg,
    h(
      'div',
      { class: 'actions' },
      h('button', { type: 'button', class: 'btn', onclick: () => dlg.close() }, 'Abbrechen'),
      h('button', { type: 'button', class: 'btn btn-primary', onclick: () => void save() }, 'Passwort speichern'),
    ),
  );
  focusFirst(dlg);
}
